/*
 * The eight characters from the mail (spec 01 §4, D-06 S19): eight boxes for the eye, **one** field
 * for everything else.
 *
 * Eight separate inputs are a screen-reader trap and a paste trap — the reason this screen had a
 * single plain field until now — so the boxes are drawn behind one real input that covers them. The
 * input keeps the label, the paste, the `one-time-code` autofill and the whole value; its own text
 * is transparent, and the cells under it show the same characters spaced out, so a person can see
 * how far they have got. It accepts `ABCD-EFGH` and `abcd efgh` alike and normalises while it is
 * typed, which is what lets a reader see the zero the server will compare rather than the O they
 * meant.
 *
 * The cells are `aria-hidden`: they are a picture of the field's value, and a reader hears the
 * value from the field itself.
 */

import { Field } from "../../../ui/index.ts"
import { t } from "../../i18n/index.ts"
import "../auth.css"
import { CODE_LENGTH, formatCode, normaliseCode } from "../code.ts"

/** The cells, counted from the code's own length rather than written out eight times. */
const CELLS = Array.from({ length: CODE_LENGTH }, (_, index) => index)

interface CodeInputProps {
	/** The canonical code, without its hyphen. */
	value: string
	onChange: (canonical: string) => void
	/** Already translated; its presence marks the field invalid. */
	error?: string
	disabled?: boolean
	autoFocus?: boolean
}

export function CodeInput({
	value,
	onChange,
	error,
	disabled = false,
	autoFocus = false,
}: CodeInputProps) {
	const typed = normaliseCode(value)
	return (
		<Field
			label={t("auth.code.label")}
			hint={t("auth.code.hint")}
			{...(error === undefined ? {} : { error })}
		>
			{(control) => (
				<span className="tb-code">
					<input
						{...control}
						type="text"
						className="tb-field__control tb-code__input"
						value={formatCode(value)}
						onChange={(e) => onChange(normaliseCode(e.target.value).slice(0, CODE_LENGTH))}
						inputMode="text"
						autoComplete="one-time-code"
						autoCapitalize="characters"
						autoCorrect="off"
						spellCheck={false}
						// The printed form is XXXX-XXXX; the hyphen is optional on the way in.
						pattern="[0-9A-HJKMNP-TV-Z]{4}-?[0-9A-HJKMNP-TV-Z]{4}"
						maxLength={CODE_LENGTH + 1}
						disabled={disabled}
						autoFocus={autoFocus}
						required
					/>
					<span className="tb-code__cells" aria-hidden="true">
						{CELLS.map((index) => (
							<span
								key={index}
								className={`tb-code__cell${index === typed.length ? " tb-code__cell--next" : ""}`}
							>
								{typed[index] ?? ""}
							</span>
						))}
					</span>
				</span>
			)}
		</Field>
	)
}
